/**
 * Enterprise view builder — a modal for composing a saved Data View from scratch:
 *   1. choose a base (a single form, or a record-centric multi-form view)
 *   2. pick columns (record-meta + form fields, across one or many forms)
 *   3. add optional filters
 *   4. name it + set visibility, then save.
 *
 * Reuses the exact column/filter editors from the live toolbar so a view built
 * here is byte-identical to one shaped inline — there is one DataViewSpec.
 */
import { useEffect, useMemo, useState } from 'react'
import { Table2, Layers, Lock, Users2, X } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { cn } from '@/lib/utils/cn'
import type { ExplorerGroup } from '../api/explorer-api'
import { useFormCatalogQuery, useFormCatalogsQuery } from '../api/use-explorer'
import { ColumnPicker } from './column-picker'
import { RecordColumnPicker } from './record-column-picker'
import { FilterBar } from './filter-bar'
import { RecordFilterBar } from './record-filter-bar'
import {
  META_COLUMNS, type DataViewSpec, type ViewColumn, type ViewFilter,
  type FormInstanceStrategy, type ViewVisibility,
} from '../types/data-view-spec'

export interface ViewBuilderPayload {
  name: string
  description: string
  visibility: ViewVisibility
  isDefault: boolean
  spec: DataViewSpec
}

interface Props {
  open: boolean
  onOpenChange: (o: boolean) => void
  groups: ExplorerGroup[]
  canManage: boolean
  saving?: boolean
  onCreate: (p: ViewBuilderPayload) => void
}

type BaseKind = 'form' | 'record'

const DEFAULT_META: ViewColumn[] = [
  { id: 'm_display', source: 'record_meta', key: 'display_id' },
  { id: 'm_status', source: 'record_meta', key: 'record_status' },
]

export function ViewBuilderDialog({ open, onOpenChange, groups, canManage, saving, onCreate }: Props) {
  const [base, setBase] = useState<BaseKind>('form')
  const [formId, setFormId] = useState<string>('')
  const [columns, setColumns] = useState<ViewColumn[]>(DEFAULT_META)
  const [formStrategies, setFormStrategies] = useState<Record<string, FormInstanceStrategy>>({})
  const [filters, setFilters] = useState<ViewFilter[]>([])
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [visibility, setVisibility] = useState<ViewVisibility>('private')
  const [isDefault, setIsDefault] = useState(false)

  const flatForms = useMemo(() => groups.flatMap((g) => g.forms), [groups])

  // Reset everything when the dialog opens.
  useEffect(() => {
    if (!open) return
    setBase('form')
    setFormId(flatForms[0]?.id ?? '')
    setColumns(DEFAULT_META)
    setFormStrategies({})
    setFilters([])
    setName(''); setDescription(''); setVisibility('private'); setIsDefault(false)
  }, [open, flatForms])

  // Switching base resets the column/filter selection (their shapes differ).
  const switchBase = (b: BaseKind) => {
    setBase(b); setColumns(DEFAULT_META); setFilters([]); setFormStrategies({})
  }

  // Single-form catalog (for the form-base column/filter editors).
  const formCatalogQ = useFormCatalogQuery(base === 'form' ? (formId || undefined) : undefined)
  const singleCatalog = formCatalogQ.data?.catalog

  // Record-centric catalogs (for the forms that contributed columns/filters).
  const recordFormIds = useMemo(
    () => (base === 'record' ? [...new Set(columns.filter((c) => c.source === 'field' && c.formId).map((c) => c.formId!))] : []),
    [base, columns],
  )
  const recordCatalogsQ = useFormCatalogsQuery(recordFormIds)
  const catalogs = recordCatalogsQ.data?.catalogs ?? {}
  const titles = recordCatalogsQ.data?.titles ?? {}

  // Reset form columns to meta defaults when the chosen form changes.
  useEffect(() => {
    if (base === 'form') { setColumns(DEFAULT_META); setFilters([]) }
  }, [formId, base])

  const fieldColumns = columns.filter((c) => c.source === 'field')
  const labelOf = (col: ViewColumn): string => {
    if (col.source === 'record_meta') return META_COLUMNS.find((m) => m.key === col.key)?.label ?? col.key
    const cat = base === 'record' ? catalogs[col.formId ?? ''] : singleCatalog
    return cat?.byKey[col.key]?.label ?? cat?.calculatedByKey[col.key]?.label.fa ?? col.key
  }
  const removeColumn = (id: string) => setColumns((cols) => cols.filter((c) => c.id !== id))

  const buildSpec = (): DataViewSpec =>
    base === 'form'
      ? {
          base: { kind: 'form', formId },
          instanceStrategy: { mode: 'long' },
          columns, filters,
          sort: { key: 'display_id', dir: 'asc' },
          headerStrategy: 'label',
        }
      : {
          base: { kind: 'record' },
          instanceStrategy: { mode: 'aggregate', aggregate: 'last' },
          formStrategies,
          columns, filters,
          sort: { key: 'display_id', dir: 'asc' },
          headerStrategy: 'label',
        }

  const canSave =
    name.trim().length > 0 &&
    fieldColumns.length > 0 &&
    (base === 'record' || !!formId)

  const submit = () => {
    if (!canSave) return
    onCreate({ name: name.trim(), description, visibility, isDefault: isDefault && canManage, spec: buildSpec() })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>ساخت نمای سفارشی</DialogTitle>
          <DialogDescription>نوع نما را انتخاب کنید، ستون‌ها و فیلترها را بچینید، سپس آن را نام‌گذاری و ذخیره کنید.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* 1. base */}
          <section className="space-y-2">
            <p className="text-[11px] font-semibold text-muted-foreground/70 uppercase">نوع نما</p>
            <div className="grid grid-cols-2 gap-2">
              <BaseCard
                active={base === 'form'} onClick={() => switchBase('form')}
                icon={Table2} title="تک‌فرمی" desc="ستون‌هایی از یک فرم"
              />
              <BaseCard
                active={base === 'record'} onClick={() => switchBase('record')}
                icon={Layers} title="رکوردمحور (چند فرم)" desc="ترکیب فیلدها از چند فرم"
              />
            </div>
            {base === 'form' && (
              <Select value={formId} onValueChange={setFormId}>
                <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="یک فرم را انتخاب کنید" /></SelectTrigger>
                <SelectContent>
                  {groups.map((g) => g.forms.map((f) => (
                    <SelectItem key={f.id} value={f.id}>{g.title} › {f.title}</SelectItem>
                  )))}
                </SelectContent>
              </Select>
            )}
          </section>

          {/* 2. columns */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-semibold text-muted-foreground/70 uppercase">ستون‌ها</p>
              {base === 'form' ? (
                singleCatalog && (
                  <ColumnPicker catalog={singleCatalog} formId={formId} columns={columns} onChange={setColumns} />
                )
              ) : (
                <RecordColumnPicker
                  groups={groups}
                  columns={columns}
                  formStrategies={formStrategies}
                  onColumnsChange={setColumns}
                  onStrategyChange={(fid, s) => setFormStrategies((m) => ({ ...m, [fid]: s }))}
                />
              )}
            </div>
            <div className="flex flex-wrap gap-1.5 min-h-9 rounded-md border border-border bg-muted/30 p-2">
              {columns.length === 0 && <span className="text-xs text-muted-foreground">ستونی انتخاب نشده.</span>}
              {columns.map((c) => (
                <span key={c.id} className="inline-flex items-center gap-1 rounded bg-background border border-border px-1.5 py-0.5 text-xs">
                  {labelOf(c)}
                  <button onClick={() => removeColumn(c.id)} className="text-muted-foreground hover:text-destructive" title="حذف ستون">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            {fieldColumns.length === 0 && <p className="text-[11px] text-amber-500">حداقل یک فیلد فرم انتخاب کنید.</p>}
          </section>

          {/* 3. filters (optional) */}
          <section className="space-y-2">
            <p className="text-[11px] font-semibold text-muted-foreground/70 uppercase">فیلترها (اختیاری)</p>
            {base === 'form' ? (
              singleCatalog
                ? <FilterBar catalog={singleCatalog} filters={filters} onChange={setFilters} />
                : <p className="text-xs text-muted-foreground">ابتدا یک فرم انتخاب کنید.</p>
            ) : recordFormIds.length > 0 ? (
              <RecordFilterBar catalogs={catalogs} titles={titles} filters={filters} onChange={setFilters} />
            ) : (
              <p className="text-xs text-muted-foreground">ابتدا چند ستون فرم انتخاب کنید.</p>
            )}
          </section>

          {/* 4. name + visibility */}
          <section className="space-y-3">
            <p className="text-[11px] font-semibold text-muted-foreground/70 uppercase">نام و دسترسی</p>
            <label className="block text-sm">
              <span className="text-muted-foreground">نام نما</span>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" placeholder="مثلاً: بیماران دیابتی نوع ۲" />
            </label>
            <label className="block text-sm">
              <span className="text-muted-foreground">توضیح (اختیاری)</span>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <VisCard active={visibility === 'private'} onClick={() => setVisibility('private')} icon={Lock} title="خصوصی" desc="فقط شما" />
              <VisCard active={visibility === 'project'} onClick={() => canManage && setVisibility('project')} icon={Users2} title="اشتراکی" desc={canManage ? 'همهٔ اعضا' : 'فقط مدیر'} disabled={!canManage} />
            </div>
            {canManage && visibility === 'project' && base === 'form' && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={isDefault} onCheckedChange={(v) => setIsDefault(!!v)} />
                <span>تنظیم به‌عنوان نمای پیش‌فرض این فرم</span>
              </label>
            )}
          </section>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>انصراف</Button>
          <Button disabled={!canSave || saving} onClick={submit}>{saving ? 'در حال ذخیره…' : 'ساخت نما'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function BaseCard({ active, onClick, icon: Icon, title, desc }: {
  active: boolean; onClick: () => void; icon: typeof Table2; title: string; desc: string
}) {
  return (
    <button
      type="button" onClick={onClick}
      className={cn('flex flex-col items-start gap-0.5 rounded-md border p-3 text-start transition-colors',
        active ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted')}
    >
      <span className="inline-flex items-center gap-1.5 text-sm font-medium"><Icon className="w-4 h-4" />{title}</span>
      <span className="text-[11px] text-muted-foreground">{desc}</span>
    </button>
  )
}

function VisCard({ active, onClick, icon: Icon, title, desc, disabled }: {
  active: boolean; onClick: () => void; icon: typeof Lock; title: string; desc: string; disabled?: boolean
}) {
  return (
    <button
      type="button" onClick={onClick} disabled={disabled}
      className={cn('flex flex-col items-start gap-0.5 rounded-md border p-2.5 text-start transition-colors',
        active ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted',
        disabled && 'opacity-50 cursor-not-allowed')}
    >
      <span className="inline-flex items-center gap-1.5 text-sm font-medium"><Icon className="w-3.5 h-3.5" />{title}</span>
      <span className="text-[11px] text-muted-foreground">{desc}</span>
    </button>
  )
}
